import { useState, useEffect } from 'react'
import posthog from 'posthog-js'
import config from 'config'
import Bugsnag from '@bugsnag/js'
import debug from 'util/debug'
import { useSelector } from 'react-redux'
import { selectCurrentUserGlobalId } from 'ducks/currentUser/selectors/base'
import { selectAccount } from 'selectors/app/selectAccount'
import storage from 'util/storage'
import selectIntegrationCredentialsForProvider from 'ducks/integrations/selectors/selectIntegrationCredentialsForProvider'

const pendingEventQueue = []
const emptyFunction = () => {}
const getPendingEventQueue = () => pendingEventQueue

const defaultTrackingContextValue = {
  initialized: false,
  identified: false,
  ready: false,
  setTrackingContextValue: emptyFunction,
  getPendingEventQueue,
  client: null,
  reset: false,
}
let globalTrackingContext = defaultTrackingContextValue

const capturePendingEvents = () => {
  const { ready, client } = globalTrackingContext
  if (!ready) {
    if (pendingEventQueue.length > 50) {
      pendingEventQueue.length = 0
    }
    return
  }
  while (pendingEventQueue.length > 0) {
    const [action, args] = pendingEventQueue.shift()
    client[action](...args)
  }
}
defaultTrackingContextValue.capturePendingEvents = capturePendingEvents

export const getTrackingContext = () => globalTrackingContext

export const useTrackingContext = () => {
  const shopifyCredentials = useSelector(state =>
    selectIntegrationCredentialsForProvider(state, { provider: 'SHOPIFY_V2' })
  )
  const rechargeCredentials = useSelector(state =>
    selectIntegrationCredentialsForProvider(state, { provider: 'RECHARGE' })
  )
  const integrationCredentials = shopifyCredentials || rechargeCredentials
  const agentId = useSelector(selectCurrentUserGlobalId)
  const { id: accountId, name, subdomain } = useSelector(selectAccount)

  const [trackingContextValue, setTrackingContextValue] = useState(
    defaultTrackingContextValue
  )
  globalTrackingContext = trackingContextValue

  const { initialized, identified, reset } = trackingContextValue

  useEffect(() => {
    setTrackingContextValue(prevTrackingContextValue => ({
      ...prevTrackingContextValue,
      setTrackingContextValue,
    }))
  }, [])

  useEffect(
    () => {
      capturePendingEvents()
    },
    [trackingContextValue]
  )

  useEffect(
    () => {
      if (initialized || !config.posthog.enabled || !integrationCredentials)
        return
      if (!subdomain || subdomain.startsWith('fullstacktest')) return

      try {
        posthog.init(config.posthog.apiKey, {
          api_host: config.posthog.apiHost,
          autocapture: false,
          capture_pageview: false,
          // Force the cookie to be tracked on the customer domain
          // instead of .groovehq.com
          cross_subdomain_cookie: false,
          disable_session_recording: false,
          secure_cookie: true,
          advanced_disable_decide: false,
          loaded: ph => {
            if (storage.get('posthogDebug')) {
              window.postHog = ph
              ph.debug()
            }
            // If we cant init posthog, mark it as initialized anyway. Broken tracking
            // should not result in a broken app
            setTrackingContextValue(prevState => ({
              ...prevState,
              initialized: true,
              client: ph,
            }))
          },
        })
      } catch (error) {
        Bugsnag.notify(error)
        debug('Unable to initialize posthog', { error })
      }
    },
    [initialized, setTrackingContextValue, subdomain, integrationCredentials]
  )

  useEffect(
    () => {
      if (
        identified ||
        reset ||
        !initialized ||
        !agentId ||
        !accountId ||
        !integrationCredentials
      )
        return

      try {
        posthog.identify(agentId)
        posthog.group('account', accountId, { name, subdomain })
        posthog.group('platform', 'web', { name: 'Web' })
      } catch (error) {
        Bugsnag.notify(error)
        debug('Unable to idenfity user in posthog', { error, agentId })
      }
      setTrackingContextValue(prevState => ({
        ...prevState,
        identified: true,
        ready: true,
      }))
    },
    [
      initialized,
      agentId,
      accountId,
      identified,
      setTrackingContextValue,
      name,
      subdomain,
      reset,
      integrationCredentials,
    ]
  )

  return {
    trackingContextValue,
  }
}
